import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { thoughtsService } from '../services/thoughtsService';
import './ThoughtPage.css';

const ThoughtPage = () => {
  const { thoughtId } = useParams();
  const [thought, setThought] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadThought = async () => {
      try {
        setLoading(true);
        const thoughtData = await thoughtsService.getThoughtById(thoughtId);
        setThought(thoughtData);
        setError(null);
        
        // Google Analytics tracking
        if (window.gtag) {
          window.gtag('event', 'view_thought', {
            'thought_id': thoughtId,
            'thought_title': thoughtData.thought,
            'author': thoughtData.author
          });
        }
      } catch (err) {
        console.error('Error loading thought:', err);
        setError('Thought not found');
      } finally {
        setLoading(false);
      }
    };

    if (thoughtId) {
      loadThought();
    }
  }, [thoughtId]);

  // Update document title and meta tags
  useEffect(() => {
    if (thought) {
      document.title = `${thought.thought} - Thought Bubbles`;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', thought.summary);
      }
    }
    
    // Cleanup on unmount
    return () => {
      document.title = 'Thought Bubbles';
    };
  }, [thought]);

  const handleVideoClick = () => {
    // Google Analytics tracking for video interaction
    if (window.gtag) {
      window.gtag('event', 'video_click', {
        'thought_id': thoughtId,
        'video_url': thought?.url
      });
    }
  };

  const extractVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const getStartTime = (url) => {
    if (!url) return 0;
    const timeMatch = url.match(/[?&]t=(\d+)([smh])?/);
    if (timeMatch) {
      const timeValue = parseInt(timeMatch[1]);
      const timeUnit = timeMatch[2] || 's';
      switch (timeUnit) {
        case 's': return timeValue;
        case 'm': return timeValue * 60;
        case 'h': return timeValue * 3600;
        default: return timeValue;
      }
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="thought-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading thought...</p>
        </div>
      </div>
    );
  }

  if (error || !thought) {
    return (
      <div className="thought-page">
        <div className="error-container">
          <h1>Thought Not Found</h1>
          <p>The thought you're looking for doesn't exist or has been removed.</p>
          <Link to="/" className="back-link">← Back to Home</Link>
        </div>
      </div>
    );
  }

  const videoId = extractVideoId(thought.url);
  const startTime = getStartTime(thought.url);

  return (
    <div className="thought-page">

      <div className="thought-container">
        <header className="thought-header">
          <Link to="/" className="back-link">← Back to All Thoughts</Link>
          <h1 className="thought-title">{thought.thought}</h1>
        </header>

        <main className="thought-content">
          <article className="thought-article">
            <div className="thought-summary">
              <p>{thought.summary}</p>
            </div>

            <div className="thought-meta">
              <div className="author-info">
                <span className="author-label">Author:</span>
                <span className="author-name">{thought.author}</span>
              </div>
              
              {thought.application && (
                <div className="application-info">
                  <h3>How to Apply This Thought</h3>
                  <p>{thought.application}</p>
                </div>
              )}
            </div>

            {videoId && (
              <div className="video-section">
                <h3>Related Video</h3>
                <div className="video-container">
                  <iframe
                    width="100%"
                    height="400"
                    src={`https://www.youtube.com/embed/${videoId}?start=${startTime}&rel=0&modestbranding=1`}
                    title={`${thought.thought} - ${thought.author}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    onClick={handleVideoClick}
                  ></iframe>
                </div>
                <p className="video-note">
                  Click the video to watch the full content related to this thought.
                </p>
              </div>
            )}

            <div className="thought-actions">
              <button 
                className="share-button"
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: thought.thought,
                      text: thought.summary,
                      url: window.location.href
                    });
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied to clipboard!');
                  }
                  
                  // Google Analytics tracking
                  if (window.gtag) {
                    window.gtag('event', 'share', {
                      'thought_id': thoughtId,
                      'method': navigator.share ? 'native' : 'clipboard'
                    });
                  }
                }}
              >
                Share This Thought
              </button>
            </div>
          </article>
        </main>

        <footer className="thought-footer">
          <p>Discover more inspiring thoughts at <Link to="/">Thought Bubbles</Link></p>
        </footer>
      </div>
    </div>
  );
};

export default ThoughtPage;
